// src/pages/admin/AdminMessagesPage.tsx
import * as React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  IconButton,
  Drawer,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useNavigate, useParams } from "react-router-dom";
import { adminFetch } from "../../services/adminFetch";

type MsgStatus = "new" | "replied" | "archived" | "spam";

type MsgRow = {
  id: number;

  name?: string | null;
  email?: string | null;
  phone?: string | null;
  subject?: string | null;
  message?: string | null;

  status?: MsgStatus | string | null;
  internal_notes?: string | null;

  locale?: string | null;
  ip?: string | null;
  user_agent?: string | null;

  created_at?: string | null;
  updated_at?: string | null;
};

function fmtDate(s?: string | null) {
  if (!s) return "-";
  return s.slice(0, 10);
}

function statusMeta(statusRaw?: string | null) {
  const status = (statusRaw || "new") as MsgStatus | string;

  switch (status) {
    case "new":
      return { label: "New", color: "info" as const, border: "info.main" };
    case "replied":
      return {
        label: "Replied",
        color: "success" as const,
        border: "success.main",
      };
    case "archived":
      return {
        label: "Archived",
        color: "default" as const,
        border: "divider",
      };
    case "spam":
      return { label: "Spam", color: "error" as const, border: "error.main" };
    default:
      return { label: status, color: "default" as const, border: "divider" };
  }
}

async function copyToClipboard(text: string) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    // ignore
  }
}

function fmtGreekDateTime(iso?: string | null) {
  if (!iso) return "-";
  const d = new Date(iso);
  return new Intl.DateTimeFormat("el-GR", {
    timeZone: "Europe/Athens",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export default function AdminMessagesPage() {
  const nav = useNavigate();
  const { locale } = useParams<{ locale?: string }>();
  const currentLocale = locale || "el";

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Filters
  const [q, setQ] = React.useState("");
  const [status, setStatus] = React.useState<string>(""); // "" = all
  const [from, setFrom] = React.useState<string>("");
  const [to, setTo] = React.useState<string>("");

  // Data
  const [items, setItems] = React.useState<MsgRow[]>([]);
  const [total, setTotal] = React.useState<number>(0);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [err, setErr] = React.useState<string | null>(null);

  // Paging
  const [limit, setLimit] = React.useState<number>(20);
  const [offset, setOffset] = React.useState<number>(0);

  // Drawer
  const [open, setOpen] = React.useState<boolean>(false);
  const [activeId, setActiveId] = React.useState<number | null>(null);
  const [active, setActive] = React.useState<MsgRow | null>(null);
  const [saving, setSaving] = React.useState<boolean>(false);
  const [saveMsg, setSaveMsg] = React.useState<string | null>(null);

  const buildQuery = React.useCallback(() => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    if (status) params.set("status", status);
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    params.set("limit", String(limit));
    params.set("offset", String(offset));
    return params.toString();
  }, [q, status, from, to, limit, offset]);

  const load = React.useCallback(async () => {
    setErr(null);
    setLoading(true);
    try {
      const qs = buildQuery();
      const res = await adminFetch(`/api/admin/messages${qs ? `?${qs}` : ""}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(data?.error || "Αποτυχία φόρτωσης μηνυμάτων.");
        setItems([]);
        setTotal(0);
        return;
      }

      const rows = (
        Array.isArray(data.items)
          ? data.items
          : Array.isArray(data.messages)
            ? data.messages
            : []
      ) as MsgRow[];
      const t = typeof data.total === "number" ? data.total : rows.length;

      const priority = (s?: string | null) => {
        if (s === "new") return 3;
        if (s === "replied") return 2;
        if (s === "archived") return 1;
        if (s === "spam") return 0;
        return 1;
      };

      const sorted = [...rows].sort((a, b) => {
        const pa = priority(String(a.status || "new"));
        const pb = priority(String(b.status || "new"));
        if (pb !== pa) return pb - pa;
        const da = a.created_at ? Date.parse(a.created_at) : 0;
        const db = b.created_at ? Date.parse(b.created_at) : 0;
        return db - da;
      });

      setItems(sorted);
      setTotal(t);
    } catch {
      setErr("Αποτυχία φόρτωσης μηνυμάτων.");
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildQuery]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openRow = async (id: number) => {
    setSaveMsg(null);
    setActiveId(id);
    setOpen(true);
    setActive(null);
    setErr(null);

    try {
      const res = await adminFetch(`/api/admin/messages/${id}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(data?.error || "Αποτυχία φόρτωσης.");
        return;
      }
      const row = (data.item ?? data.message ?? data) as MsgRow;
      setActive(row);
    } catch {
      setErr("Αποτυχία φόρτωσης.");
    }
  };

  const closeDrawer = () => {
    setOpen(false);
    setActiveId(null);
    setActive(null);
    setSaveMsg(null);
  };

  const setActiveField = (k: keyof MsgRow, v: any) => {
    setActive((s) => (s ? { ...s, [k]: v } : s));
  };

  const saveActive = async () => {
    if (!activeId || !active) return;
    setSaving(true);
    setSaveMsg(null);
    setErr(null);

    try {
      const res = await adminFetch(`/api/admin/messages/${activeId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          status: active.status || "new",
          internal_notes: active.internal_notes || "",
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr(data?.error || "Αποτυχία αποθήκευσης.");
        return;
      }

      setSaveMsg("Αποθηκεύτηκε.");
      setItems((prev) =>
        prev.map((r) =>
          r.id === activeId
            ? { ...r, status: active.status, internal_notes: active.internal_notes }
            : r,
        ),
      );
    } catch {
      setErr("Αποτυχία αποθήκευσης.");
    } finally {
      setSaving(false);
    }
  };

  const pageInfo = {
    from: total === 0 ? 0 : offset + 1,
    to: Math.min(offset + limit, total || offset + items.length),
  };

  return (
    <Stack spacing={2}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        gap={2}
      >
        <Stack spacing={0.2}>
          <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
            Admin · Messages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Μηνύματα φόρμας επικοινωνίας (new / replied / archived / spam)
          </Typography>
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "space-between", md: "flex-end" }}>
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="outlined"
            onClick={() => nav(`/${currentLocale}/admin/properties`)}
            sx={{ whiteSpace: "nowrap" }}
          >
            Properties
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ md: "center" }}
          >
            <TextField
              label="Search (name/email/phone/subject)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              fullWidth
              size="small"
            />

            <FormControl sx={{ minWidth: isMobile ? "100%" : 180 }} size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={status}
                onChange={(e) => setStatus(String(e.target.value))}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="new">new</MenuItem>
                <MenuItem value="replied">replied</MenuItem>
                <MenuItem value="archived">archived</MenuItem>
                <MenuItem value="spam">spam</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="From (created)"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: isMobile ? "100%" : 170 }}
              size="small"
            />
            <TextField
              label="To (created)"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: isMobile ? "100%" : 170 }}
              size="small"
            />

            <Button
              fullWidth={isMobile}
              variant="contained"
              onClick={() => {
                setOffset(0);
                load();
              }}
              disabled={loading}
              sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
            >
              {loading ? "Loading..." : "Apply"}
            </Button>
          </Stack>

          <Divider />

          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ md: "center" }}
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="body2" color="text.secondary">
              Σύνολο: <b>{total}</b> · Εμφάνιση: <b>{pageInfo.from}</b>–<b>{pageInfo.to}</b>
            </Typography>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: "space-between", md: "flex-end" }}>
              <TextField
                label="Per page"
                type="number"
                value={limit}
                onChange={(e) => {
                  const v = Math.max(5, Math.min(200, Number(e.target.value || 20)));
                  setLimit(v);
                  setOffset(0);
                }}
                sx={{ width: 120 }}
                inputProps={{ min: 5, max: 200 }}
                size="small"
              />

              <Button
                variant="outlined"
                disabled={offset <= 0 || loading}
                onClick={() => setOffset((o) => Math.max(0, o - limit))}
              >
                Prev
              </Button>
              <Button
                variant="outlined"
                disabled={loading || offset + limit >= (total || 0)}
                onClick={() => setOffset((o) => o + limit)}
              >
                Next
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {err ? <Alert severity="error">{err}</Alert> : null}

      {/* Results */}
      <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
        {isMobile ? (
          <Stack spacing={1.5} sx={{ p: 1.5 }}>
            {loading ? (
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2, px: 1 }}>
                <CircularProgress size={18} />
                <Typography color="text.secondary">Φόρτωση…</Typography>
              </Stack>
            ) : null}

            {!loading && items.length === 0 ? (
              <Typography color="text.secondary" sx={{ py: 2, px: 1 }}>
                Δεν υπάρχουν αποτελέσματα με αυτά τα φίλτρα.
              </Typography>
            ) : null}

            {!loading &&
              items.map((r) => {
                const meta = statusMeta(String(r.status || "new"));
                return (
                  <Card
                    key={r.id}
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      borderLeft: "4px solid",
                      borderLeftColor: meta.border,
                      overflow: "hidden",
                    }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
                          <Chip
                            size="small"
                            label={meta.label}
                            color={meta.color as any}
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                            {fmtGreekDateTime(r.created_at)}
                          </Typography>
                        </Stack>

                        <Box>
                          <Typography sx={{ fontWeight: 900 }}>
                            {r.subject?.trim() ? r.subject : "—"}
                          </Typography>

                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {r.name || "-"}
                          </Typography>

                          <Typography variant="caption" color="text.secondary" sx={{ display: "block", wordBreak: "break-word" }}>
                            {r.email || "-"} {r.phone ? `· ${r.phone}` : ""}
                          </Typography>
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                          {(r.message || "").slice(0, 120)}
                          {(r.message || "").length > 120 ? "…" : ""}
                        </Typography>

                        <Button
                          variant="contained"
                          onClick={() => openRow(r.id)}
                          fullWidth
                          startIcon={<VisibilityIcon />}
                          sx={{ fontWeight: 900, borderRadius: 2 }}
                        >
                          View / Edit
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
          </Stack>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>Status</TableCell>
                  <TableCell>Sender</TableCell>
                  <TableCell>Subject</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>Created</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
                        <CircularProgress size={18} />
                        <Typography color="text.secondary">Φόρτωση…</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ) : null}

                {!loading && items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary" sx={{ py: 2 }}>
                        Δεν υπάρχουν αποτελέσματα με αυτά τα φίλτρα.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}

                {!loading &&
                  items.map((r) => {
                    const meta = statusMeta(String(r.status || "new"));
                    const highlight = r.status === "new" ? "rgba(0,0,0,0.02)" : "transparent";

                    return (
                      <TableRow
                        key={r.id}
                        hover
                        sx={{
                          bgcolor: highlight,
                          "& td": {
                            borderLeft: `3px solid`,
                            borderLeftColor: meta.border,
                          },
                        }}
                      >
                        <TableCell sx={{ width: 140 }}>
                          <Chip
                            size="small"
                            label={meta.label}
                            color={meta.color as any}
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 800 }}>{r.name || "-"}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.email || "-"} {r.phone ? `· ${r.phone}` : ""}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Typography sx={{ fontWeight: 800 }}>
                            {r.subject?.trim() ? r.subject : "—"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {(r.message || "").slice(0, 90)}
                            {(r.message || "").length > 90 ? "…" : ""}
                          </Typography>
                        </TableCell>

                        <TableCell sx={{ whiteSpace: "nowrap" }}>
                          {fmtGreekDateTime(r.created_at)}
                        </TableCell>

                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="View / Edit">
                            <span>
                              <IconButton onClick={() => openRow(r.id)}>
                                <VisibilityIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </Box>
        )}
      </Paper>

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: 460 },
            maxWidth: "100vw",
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
            <Stack>
              <Typography sx={{ fontWeight: 900 }}>Message Details</Typography>
              <Typography variant="caption" color="text.secondary">
                #{activeId ?? "-"}
              </Typography>
            </Stack>
            <IconButton onClick={closeDrawer}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider sx={{ my: 1.5 }} />

          {saveMsg ? (
            <Alert severity="success" sx={{ mb: 1.5 }}>
              {saveMsg}
            </Alert>
          ) : null}
          {err ? (
            <Alert severity="error" sx={{ mb: 1.5 }}>
              {err}
            </Alert>
          ) : null}

          {!active ? (
            <Stack sx={{ py: 3 }} alignItems="center" spacing={1}>
              <CircularProgress size={22} />
              <Typography color="text.secondary">Loading…</Typography>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 2 }}>
                <Stack spacing={1}>
                  <Typography sx={{ fontWeight: 900 }}>Sender</Typography>

                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "flex-start" }} gap={1}>
                    <Box>
                      <Typography sx={{ fontWeight: 800 }}>{active.name || "-"}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: "break-word" }}>
                        {active.email || "-"} {active.phone ? `· ${active.phone}` : ""}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Copy email">
                        <span>
                          <IconButton
                            onClick={() => copyToClipboard(active.email || "")}
                            disabled={!active.email}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Copy phone">
                        <span>
                          <IconButton
                            onClick={() => copyToClipboard(active.phone || "")}
                            disabled={!active.phone}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <Typography variant="caption" color="text.secondary">
                    Locale: {active.locale || "-"} · IP: {active.ip || "-"}
                  </Typography>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 2 }}>
                <Stack spacing={1}>
                  <Typography sx={{ fontWeight: 900 }}>Subject</Typography>
                  <Typography sx={{ fontWeight: 800, wordBreak: "break-word" }}>
                    {active.subject || "—"}
                  </Typography>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 2 }}>
                <Stack spacing={1}>
                  <Typography sx={{ fontWeight: 900 }}>Message</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {active.message || "—"}
                  </Typography>
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.6, borderRadius: 2 }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography sx={{ fontWeight: 900 }}>Admin Actions</Typography>
                    <Chip
                      size="small"
                      label={statusMeta(String(active.status || "new")).label}
                      color={statusMeta(String(active.status || "new")).color as any}
                      variant="outlined"
                    />
                  </Stack>

                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      label="Status"
                      value={String(active.status || "new")}
                      onChange={(e) => setActiveField("status", e.target.value as MsgStatus)}
                    >
                      <MenuItem value="new">new</MenuItem>
                      <MenuItem value="replied">replied</MenuItem>
                      <MenuItem value="archived">archived</MenuItem>
                      <MenuItem value="spam">spam</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Internal notes"
                    multiline
                    minRows={3}
                    value={active.internal_notes || ""}
                    onChange={(e) => setActiveField("internal_notes", e.target.value)}
                    size="small"
                  />

                  <Button
                    startIcon={<SaveIcon />}
                    variant="contained"
                    onClick={saveActive}
                    disabled={saving}
                    sx={{ fontWeight: 900, borderRadius: 2 }}
                    fullWidth
                  >
                    {saving ? "Saving..." : "Save"}
                  </Button>

                  <Typography variant="caption" color="text.secondary">
                    Created: {fmtDate(active.created_at)} · Updated: {fmtDate(active.updated_at)}
                  </Typography>
                </Stack>
              </Paper>
            </Stack>
          )}
        </Box>
      </Drawer>
    </Stack>
  );
}
